/**
 * The route table.
 *
 * `createBrowserRouter` — the data-router mode — for two things this app genuinely needs: a route
 * `ErrorBoundary`, which is what stops a render error being a white screen, and a layout route,
 * so the shell is not re-mounted on every navigation.
 *
 * Data loading is **not** done in loaders. TanStack Query owns every server read (dedup, caching,
 * `staleTime`, background refetch, retry), and splitting that across loaders and the query cache
 * would mean two systems holding the same data with different ideas about when it is stale.
 *
 * Every screen is `React.lazy`, so the initial bundle carries the shell and the catalog and
 * nothing else. The gallery is the clearest case: it imports the whole design system's demo
 * surface and must never reach a production bundle at all.
 *
 * **Everything is gated except signing in.**
 *
 * `/login` and `/invite/:token` are the only routes an anonymous visitor can reach; everything
 * else sits under a pathless `ProtectedRoute` layout, so the first thing a visitor sees is the
 * sign-in screen. This reverses prompt 11, which made the catalog, course detail and pathways
 * public on the grounds that a licence catalogue nobody can browse is a catalogue nobody browses.
 * The product decision changed — no content is visible outside authentication, metadata included
 * — and the API enforces the same rule, so gating only these routes would be decoration
 * (00-context §10.1).
 *
 * `/admin/*` keeps the stricter `AdminRoute`, which refuses an authenticated non-admin in place
 * rather than redirecting them to a sign-in page they have already used.
 */
import { lazy } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';

import { AppShell } from './components/AppShell.js';
import { RootErrorBoundary } from './components/RootErrorBoundary.js';
import { AuthProvider } from './auth/AuthProvider.js';
import { AdminRoute, ProtectedRoute } from './auth/ProtectedRoute.js';

const Courses = lazy(async () => import('./routes/Courses.js'));
const CourseDetail = lazy(async () => import('./routes/CourseDetail.js'));
const Lesson = lazy(async () => import('./routes/Lesson.js'));
const Pathways = lazy(async () => import('./routes/Pathways.js'));
const PathwayDetail = lazy(async () => import('./routes/PathwayDetail.js'));
const Login = lazy(async () => import('./routes/Login.js'));
const AcceptInvite = lazy(async () => import('./routes/AcceptInvite.js'));
const Search = lazy(async () => import('./routes/Search.js'));
const AdminLayout = lazy(async () => import('./routes/admin/AdminLayout.js'));
const AdminUsers = lazy(async () => import('./routes/admin/Users.js'));
const AdminInvites = lazy(async () => import('./routes/admin/Invites.js'));
const AdminContent = lazy(async () => import('./routes/admin/Content.js'));
const AdminQueries = lazy(async () => import('./routes/admin/Queries.js'));
const NotFound = lazy(async () => import('./routes/NotFound.js'));

/**
 * The component gallery, in development builds only — and the mechanism matters.
 *
 * The obvious spelling is a `React.lazy` beside the others plus
 * `import.meta.env.DEV ? [route] : []`, and it **does not work**. `import.meta.env.DEV` is
 * replaced with a literal, so the *route* disappears from a production build — but the
 * `lazy(() => import('./routes/UiGallery.js'))` call sits at module scope and is still live, so
 * the import stays in the graph, and with it the whole of `ComponentGallery`. Measured: 35 kB of
 * demo surface in a shared chunk of the production bundle, reachable by nobody.
 *
 * Putting the dynamic import *inside* the dead branch is what lets Rollup drop it. React Router's
 * own route-level `lazy` is the natural way to express that — it is the one place in this file
 * that does not use `React.lazy`, for this reason.
 */
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      {
        path: 'dev/ui',
        lazy: async () => {
          const { default: UiGallery } = await import('./routes/UiGallery.js');

          return { element: <UiGallery /> };
        },
      },
    ]
  : [];

export const routes: RouteObject[] = [
  {
    path: '/',
    /*
     * `AuthProvider` is the shell's parent, not the application's.
     *
     * It calls `useNavigate` and `useLocation` — it owns the global 401 redirect — so it has to
     * be inside the router. Mounting it as the root route's element is what puts it there while
     * still leaving it above every screen. The `ErrorBoundary` on this same route catches
     * anything it throws.
     */
    element: (
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    ),
    ErrorBoundary: RootErrorBoundary,
    children: [
      // `replace` so the redirect does not leave an entry the back button lands on, which would
      // bounce straight forward again.
      { index: true, element: <Navigate to="/courses" replace /> },

      // The only two routes reachable without a session. Both have to be: one is how you get
      // one, and the other is how an invited account is created in the first place.
      { path: 'login', element: <Login /> },
      { path: 'invite/:token', element: <AcceptInvite /> },

      /**
       * One pathless layout route carrying the guard, rather than a wrapper per screen.
       *
       * A screen added as a child of this route is gated by construction; a screen added as a
       * sibling of it is public by construction. Making the two cases structurally different is
       * the point — the previous arrangement wrapped `/search` individually, which works right
       * up until somebody adds a route and forgets.
       */
      {
        element: <ProtectedRoute />,
        children: [
          { path: 'courses', element: <Courses /> },
          { path: 'courses/:slugOrIsbn', element: <CourseDetail /> },
          { path: 'courses/:slugOrIsbn/lessons/:lessonId', element: <Lesson /> },

          { path: 'pathways', element: <Pathways /> },
          { path: 'pathways/:slug', element: <PathwayDetail /> },

          { path: 'search', element: <Search /> },
        ],
      },

      /**
       * The admin area, as a layout route with four children.
       *
       * `AdminRoute` wraps the *layout*, so the guard runs once for the whole area rather than
       * four times with four chances to forget one — and a route added under here later is gated
       * by construction. It refuses an authenticated non-admin in place rather than redirecting:
       * sending someone to sign in when they are already signed in is a loop.
       *
       * `/admin` itself redirects to `/admin/users`, so the user menu's "Admin area" item and a
       * hand-typed `/admin` both land somewhere real instead of on an empty layout.
       */
      {
        path: 'admin',
        element: (
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/admin/users" replace /> },
          { path: 'users', element: <AdminUsers /> },
          { path: 'invites', element: <AdminInvites /> },
          { path: 'content', element: <AdminContent /> },
          { path: 'queries', element: <AdminQueries /> },
        ],
      },

      ...devRoutes,

      { path: '*', element: <NotFound /> },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
